import { constantToDisplayName } from "../core/constantToDisplayName";
import { renderProductIcon } from 'legoland-sdk';
import { useMemo, useState } from 'react';
import { useTable } from 'react-table';
import styled, { css } from 'styled-components';
import { Box, Button, Heading, Input, ModalActions, ModalHeader, ModalLayout, Text, ModalContent as TombacModalContent, TooltipOnHover, propStyling, tombac, useTombac } from 'tombac';
import { SearchIcon } from 'tombac-icons';
import { useUserListReportQuery } from "../users/api/userManagementApiHooks";
import { Pagination } from "../contracts/list/Pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "../contracts/list/Table";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const CopyReportsForm = ({
  report,
  onCancel,
  onSubmitCopy
}) => {
  const tombac = useTombac();
  const [userDestination, setUserDestination] = useState(null);
  const shouldTransferReport = report?.product.id === 'ctm' || report?.product.id === 'ja';
  const columns = useMemo(() => [{
    Header: 'Email',
    id: 'email',
    accessor: 'email',
    Cell: ({
      value,
      row
    }) => /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UserEmail, {
        children: value
      }), /*#__PURE__*/_jsx(UserOrg, {
        children: row.original.organization.name
      })]
    })
  }, {
    Header: 'Role',
    id: 'role',
    accessor: 'role',
    width: 50,
    Cell: ({
      value
    }) => /*#__PURE__*/_jsx(Text, {
      children: constantToDisplayName(value)
    })
  }, {
    Header: 'Products',
    id: 'products',
    accessor: 'products',
    width: 140,
    Cell: ({
      value
    }) => /*#__PURE__*/_jsx(ProductsList, {
      children: value.filter(p => p.access.status !== 'DENIED').map(product => {
        const isExpired = product.access.status === 'EXPIRED';
        const isPending = product.access.status === 'PENDING';
        return /*#__PURE__*/_jsx(TooltipOnHover, {
          content: `${product.displayName} ${isExpired ? '| EXPIRED' : isPending ? '| PENDING' : ''}`,
          placement: "top",
          size: "s",
          children: /*#__PURE__*/_jsx(Product, {
            expired: isExpired || isPending,
            children: renderProductIcon(product.id)
          }, product.id)
        }, product.id);
      })
    })
  }, {
    id: 'actions',
    width: 40,
    Cell: ({
      row
    }) => {
      const isCopyAllowed = row.original.products.some(o => o.id === report?.product.id && o.access.status === 'GRANTED');
      return /*#__PURE__*/_jsx(Radio, {
        name: "userDest",
        value: row.original.email,
        readOnly: true,
        checked: userDestination && userDestination.email === row.original.email,
        disabled: !isCopyAllowed
      });
    }
  }], [userDestination]);
  const [page, setPage] = useState({
    index: 0,
    size: 20
  });
  const [filters, setFilters] = useState({
    products: [report?.product.id],
    roles: [],
    accessTypes: [],
    searchValues: '',
    statuses: ['ACTIVE']
  });
  const sort = {
    property: 'createdAt',
    isAscending: false
  };
  const {
    data
  } = useUserListReportQuery({
    filters,
    sort,
    page
  }, {
    keepPreviousData: true
  });
  const tableData = useMemo(() => data?.content ?? [], [data]);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({
    columns: columns,
    data: tableData
  });
  if (!report) return null;
  return /*#__PURE__*/_jsxs(ModalLayout, {
    $width: "900u",
    "data-sentry-element": "ModalLayout",
    "data-sentry-component": "CopyReportsForm",
    "data-sentry-source-file": "CopyReportsForm.tsx",
    children: [/*#__PURE__*/_jsxs(ModalHeader, {
      $m: "4sp 5sp 0",
      fontSize: 32,
      fontWeight: 500,
      "data-sentry-element": "ModalHeader",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ModalHeader",
      children: [shouldTransferReport ? 'Move' : 'Copy', " to another user"]
    }), /*#__PURE__*/_jsxs(ContractSummary, {
      "data-sentry-element": "ContractSummary",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ContractSummary",
      children: [/*#__PURE__*/_jsx(Box, {
        $flex: "none",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Box",
        children: renderProductIcon(report.product.id)
      }), /*#__PURE__*/_jsxs(Box, {
        $flex: "auto",
        $ml: "2sp",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Box",
        children: [/*#__PURE__*/_jsx(Heading, {
          as: "div",
          level: 5,
          "data-sentry-element": "Heading",
          "data-sentry-source-file": "CopyReportsForm.tsx",
          "data-sentry-element": "Heading",
          children: report.name
        }), /*#__PURE__*/_jsx(Text, {
          $mt: "1sp",
          variant: "secondary",
          "data-sentry-element": "Text",
          "data-sentry-source-file": "CopyReportsForm.tsx",
          "data-sentry-element": "Text",
          children: report.id
        })]
      })]
    }), /*#__PURE__*/_jsxs(ModalContent, {
      $mt: "3sp",
      "data-sentry-element": "ModalContent",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ModalContent",
      children: [/*#__PURE__*/_jsxs(Heading, {
        $mb: "1sp",
        as: "div",
        level: 4,
        "data-sentry-element": "Heading",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Heading",
        children: ["Select the user you want to ", shouldTransferReport ? 'move' : 'copy', ' ', "your data to"]
      }), /*#__PURE__*/_jsx(Text, {
        $mb: "2sp",
        $color: tombac.color('neutral', 600),
        "data-sentry-element": "Text",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Text",
        children: "List of all the users of the organization, please select the one you want to copy your data to"
      }), /*#__PURE__*/_jsx(Input, {
        $width: "100%",
        $flex: "1",
        $mb: "2sp",
        onChange: event => setFilters({
          ...filters,
          searchValues: event.currentTarget.value
        }),
        placeholder: "Search user",
        prepend: /*#__PURE__*/_jsx(SearchIcon, {}),
        type: "text",
        value: filters.searchValues,
        "data-sentry-element": "Input",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Input"
      }), /*#__PURE__*/_jsx(Box, {
        $boxSizing: "content-box",
        $maxWidth: "1280u",
        $padding: '0',
        $width: "100%",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Box",
        children: /*#__PURE__*/_jsx(Pagination, {
          page: page,
          itemCount: data?.totalElements,
          onPageChange: page => {
            setPage(page);
          },
          "data-sentry-element": "Pagination",
          "data-sentry-source-file": "CopyReportsForm.tsx",
          "data-sentry-element": "Pagination"
        })
      })]
    }), /*#__PURE__*/_jsx(ModalContent, {
      variant: "scrollable",
      "data-sentry-element": "ModalContent",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ModalContent",
      children: /*#__PURE__*/_jsx(Box, {
        $boxSizing: "content-box",
        $margin: "3sp auto",
        $maxWidth: "1280u",
        $padding: '0',
        $width: "100%",
        "data-sentry-element": "Box",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Box",
        children: tableData.length === 0 ? /*#__PURE__*/_jsx(Box, {
          $display: "flex",
          $justifyContent: "center",
          $mt: "3sp",
          children: /*#__PURE__*/_jsx(Text, {
            variant: "secondary"
          })
        }) : /*#__PURE__*/_jsxs(Table, {
          ...getTableProps(),
          children: [/*#__PURE__*/_jsx(Thead, {
            children: headerGroups.map(headerGroup => {
              const {
                key: headerGroupKey,
                ...headerGroupProps
              } = headerGroup.getHeaderGroupProps();
              return /*#__PURE__*/_jsx(Tr, {
                ...headerGroupProps,
                children: headerGroup.headers.map(column => {
                  const {
                    key: headerKey,
                    ...headerProps
                  } = column.getHeaderProps({
                    style: {
                      width: tombac.unit(Number(column.width))
                    }
                  });
                  return /*#__PURE__*/_jsx(Th, {
                    ...headerProps,
                    children: column.render('Header')
                  }, headerKey);
                })
              }, headerGroupKey);
            })
          }), /*#__PURE__*/_jsx(Tbody, {
            ...getTableBodyProps(),
            children: rows.map(row => {
              prepareRow(row);
              const shouldBeDisabled = row.original.products.some(o => o.id === report?.product.id && o.access.status !== 'GRANTED');
              const {
                key: rowKey,
                ...rowProps
              } = row.getRowProps();
              return /*#__PURE__*/_jsx(TableRow, {
                ...rowProps,
                $height: "64u",
                ...(!shouldBeDisabled && {
                  onClick: () => setUserDestination(row.original)
                }),
                $disabled: shouldBeDisabled,
                children: row.cells.map(cell => {
                  const {
                    key: cellKey,
                    ...cellProps
                  } = cell.getCellProps();
                  return /*#__PURE__*/_jsx(Td, {
                    $overflow: "hidden",
                    $whiteSpace: "nowrap",
                    ...cellProps,
                    children: cell.render('Cell')
                  }, cellKey);
                })
              }, rowKey);
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsx(ModalContent, {
      "data-sentry-element": "ModalContent",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ModalContent",
      children: /*#__PURE__*/_jsxs(Heading, {
        as: "div",
        level: 4,
        "data-sentry-element": "Heading",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Heading",
        children: ["User selected: ", userDestination ? userDestination.email : 'none']
      })
    }), /*#__PURE__*/_jsxs(ModalActions, {
      $m: "3sp 5sp 5sp",
      "data-sentry-element": "ModalActions",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "ModalActions",
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: onCancel,
        type: "button",
        variant: "flat",
        "data-sentry-element": "Button",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Button",
        children: "Close"
      }), /*#__PURE__*/_jsx(Button, {
        disabled: !userDestination,
        onClick: () => onSubmitCopy(userDestination, shouldTransferReport),
        type: "button",
        variant: "primary",
        "data-sentry-element": "Button",
        "data-sentry-source-file": "CopyReportsForm.tsx",
        "data-sentry-element": "Button",
        children: shouldTransferReport ? 'Move' : 'Copy'
      })]
    })]
  });
};
const TableRow = styled(Tr).withConfig({
  displayName: "CopyReportsForm__TableRow"
})(["", ""], ({
  $disabled
}) => $disabled && css(["background-color:", ";cursor:not-allowed;&:hover{background:", ";}"], tombac.color('neutral', 300), tombac.color('neutral', 300)));
const ContractSummary = styled.div.withConfig({
  displayName: "CopyReportsForm__ContractSummary"
})(["align-items:center;border:", ";display:flex;margin:", ";padding:", ";"], tombac.parse('1u solid --neutral-400'), tombac.space(3, 5, 0), tombac.space(2));
const ModalContent = styled(TombacModalContent).withConfig({
  displayName: "CopyReportsForm__ModalContent"
})(["", " ", ""], tombac.variant('variant', {
  default: css(["margin-left:", ";margin-right:", ";"], tombac.space(5), tombac.space(5)),
  scrollable: css(["padding-left:", ";padding-right:", ";"], tombac.space(5), tombac.space(5))
}), propStyling);
const UserEmail = styled.span.withConfig({
  displayName: "CopyReportsForm__UserEmail"
})(["display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:300px;", ";"], tombac.text());
const UserOrg = styled.span.withConfig({
  displayName: "CopyReportsForm__UserOrg"
})(["flex:auto;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:300px;", ";"], tombac.text({
  color: tombac.color('neutral', 600),
  fontSize: 12
}));
const ProductsList = styled.div.withConfig({
  displayName: "CopyReportsForm__ProductsList"
})(["display:flex;align-items:center;padding:", ";", ""], tombac.space(1, 3), propStyling);
const Product = styled.div.withConfig({
  displayName: "CopyReportsForm__Product"
})(["margin-right:10px;", " svg{opacity:", ";width:35px;}"], props => props.expired && `
        filter: grayscale(1);
        opacity: 0.8;
    `, props => props.expired ? 0.5 : 1);
const Mark = styled.span.withConfig({
  displayName: "CopyReportsForm__Mark"
})(["display:inline-block;position:relative;border:1px solid ", ";width:18px;height:18px;left:0;border-radius:50%;margin-right:5px;vertical-align:middle;&::after{content:'';display:block;width:0;height:0;border-radius:50%;background-color:#03a9f4;opacity:0;left:50%;top:50%;position:absolute;transition:all 110ms;}"], tombac.color('neutral', 400));
const InputRadio = styled.input.withConfig({
  displayName: "CopyReportsForm__InputRadio"
})(["position:absolute;visibility:hidden;display:none;&:checked + ", "{border:1px solid #03a9f4;&::after{width:10px;height:10px;opacity:1;left:calc(50% - 5px);top:calc(50% - 5px);}}"], Mark);
const Label = styled.label.withConfig({
  displayName: "CopyReportsForm__Label"
})(["display:block;cursor:pointer;padding:5px 10px 5px 0;position:relative;", "{&:disabled{cursor:not-allowed;}}"], InputRadio);
const RadioWrapper = styled.div.withConfig({
  displayName: "CopyReportsForm__RadioWrapper"
})(["display:block;text-align:right;", "{", "}", "{", "}"], Label, ({
  disabled
}) => disabled && `cursor: not-allowed`, Mark, ({
  disabled
}) => disabled && css(["background-color:", ";"], tombac.color('neutral', 400)));
const Radio = ({
  name,
  onChange,
  children,
  checked,
  value,
  disabled,
  readOnly
}) => /*#__PURE__*/_jsx(RadioWrapper, {
  disabled: disabled,
  "data-sentry-element": "RadioWrapper",
  "data-sentry-component": "Radio",
  "data-sentry-source-file": "CopyReportsForm.tsx",
  children: /*#__PURE__*/_jsxs(Label, {
    "data-sentry-element": "Label",
    "data-sentry-source-file": "CopyReportsForm.tsx",
    "data-sentry-element": "Label",
    children: [/*#__PURE__*/_jsx(InputRadio, {
      name: name,
      type: "radio",
      onChange: onChange,
      checked: checked,
      value: value,
      disabled: disabled,
      readOnly: readOnly,
      "data-sentry-element": "InputRadio",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "InputRadio"
    }), /*#__PURE__*/_jsx(Mark, {
      "data-sentry-element": "Mark",
      "data-sentry-source-file": "CopyReportsForm.tsx",
      "data-sentry-element": "Mark"
    }), children]
  })
});